import type { DraftExercise } from './workoutSession';

export type WorkoutSessionDraft = {
  workoutName: string;
  activityType: string;
  sessionStartedLocal: string;
  durationMinutesStr: string;
  sessionNotes: string;
  exercises: DraftExercise[];
  sessionStartMs: number;
  savedAt: string;
};

const PREFIX = 'preformiq_workout_draft';

function key(userId: string | undefined): string {
  return `${PREFIX}:${userId ?? 'anon'}`;
}

export function loadWorkoutSessionDraft(userId: string | undefined): WorkoutSessionDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return null;
    const d = JSON.parse(raw) as WorkoutSessionDraft;
    if (!d?.savedAt || !Array.isArray(d.exercises)) return null;
    const ageMs = Date.now() - new Date(d.savedAt).getTime();
    if (ageMs > 7 * 24 * 60 * 60 * 1000) {
      clearWorkoutSessionDraft(userId);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function saveWorkoutSessionDraft(userId: string | undefined, draft: Omit<WorkoutSessionDraft, 'savedAt'>): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: WorkoutSessionDraft = { ...draft, savedAt: new Date().toISOString() };
    localStorage.setItem(key(userId), JSON.stringify(payload));
  } catch {
    /* quota */
  }
}

export function clearWorkoutSessionDraft(userId: string | undefined): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key(userId));
  } catch {
    /* ignore */
  }
}

export function draftHasWork(draft: WorkoutSessionDraft): boolean {
  if (draft.sessionNotes.trim()) return true;
  if (draft.workoutName.trim()) return true;
  return draft.exercises.some((ex) => ex.name.trim() || ex.sets.some((s) => s.completed || s.reps || s.weight));
}
